'use client'

import { useMemo, useState } from 'react'
import { useRouter } from 'next/navigation'

import { AppShell } from '@/components/layout/AppShell'
import { PageHeader } from '@/components/layout/PageHeader'
import { EmptyState } from '@/components/common/EmptyState'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useI18n } from '@/lib/hooks/use-i18n'
import { useAgents } from '@/lib/hooks/use-agents'
import type { Agent } from '@/lib/types/agents'
import { useToast } from '@/lib/hooks/use-toast'
import { AgentCard } from './components/AgentCard'
import { Bot, RefreshCw, Search, X } from 'lucide-react'

function resolveLabel(t: (key: string) => string, key: string, fallback: string) {
  const label = t(key)
  return label === key ? fallback : label
}

function pickDownloadUrl(downloadInfo: Agent['launch_info']): string | null {
  if (!downloadInfo) return null

  const platform = typeof navigator !== 'undefined' ? navigator.platform.toLowerCase() : ''
  if (platform.includes('win')) return downloadInfo.windows || null
  if (platform.includes('mac')) return downloadInfo.mac || null
  if (platform.includes('android')) return downloadInfo.android || null
  if (platform.includes('iphone') || platform.includes('ipad') || platform.includes('ios')) {
    return downloadInfo.ios || null
  }
  return downloadInfo.windows || downloadInfo.mac || downloadInfo.android || downloadInfo.ios || null
}

export default function AgentsPage() {
  const { t } = useI18n()
  const { toast } = useToast()
  const router = useRouter()
  const { data: agents, isLoading, isError, isFetching, refetch } = useAgents()

  const [searchKeyword, setSearchKeyword] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')

  const categories = useMemo(() => {
    const unique = new Set<string>()
    ;(agents ?? []).forEach((agent) => {
      if (agent.category) unique.add(agent.category)
    })
    return Array.from(unique).sort((a, b) => a.localeCompare(b))
  }, [agents])

  const filteredAgents = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase()
    let result = agents ?? []

    if (keyword) {
      result = result.filter((agent) => {
        const haystack = `${agent.name} ${agent.description}`.toLowerCase()
        return haystack.includes(keyword)
      })
    }

    if (selectedCategory !== 'all') {
      result = result.filter((agent) => agent.category === selectedCategory)
    }

    return result
  }, [agents, searchKeyword, selectedCategory])

  const handleLaunch = (agent: Agent) => {
    toast({
      title: t('agents.launch'),
      description: t('agents.launch.toast', { name: agent.name }),
    })
      console.log(agent)
    if (agent.launch_type === 'inner') {
        if (agent.launch_info?.url) {
            router.push(`/agents${agent.launch_info.url}`)
        }
    } else if (agent.launch_type === 'external') {
        if (agent.launch_info?.url) {
            window.open(agent.launch_info.url, '_blank', 'noopener,noreferrer')
        }
    }

  }

  const handleDownload = (agent: Agent) => {
    if (agent.launch_type !== 'download') {
      toast({
        title: t('agents.download'),
        description: t('agents.download.missing'),
        variant: 'destructive',
      })
      return
    }

    const url = pickDownloadUrl(agent.launch_info)
    if (!url) {
      toast({
        title: t('agents.download'),
        description: t('agents.download.unsupported'),
        variant: 'destructive',
      })
      return
    }

    window.open(url, '_blank', 'noopener,noreferrer')
    toast({
      title: t('agents.download'),
      description: t('agents.download.started'),
    })
  }

  return (
    <AppShell>
      <div className="flex flex-col h-full">
        <PageHeader title={t('agents.title')} />

        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">

            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative w-72 max-w-full">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    placeholder={t('agents.searchPlaceholder')}
                    className="pl-9 pr-9"
                  />
                  {searchKeyword && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                      onClick={() => setSearchKeyword('')}
                      aria-label={t('agents.searchClear')}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-44">
                    <SelectValue placeholder={t('agents.categoryPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('agents.category.all')}</SelectItem>
                    {categories.map((category) => (
                      <SelectItem key={category} value={category}>
                        {resolveLabel(t, `agents.category.${category}`, category)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                size="icon"
                variant="outline"
                onClick={() => refetch()}
                disabled={isFetching}
                aria-label={t('agents.refresh')}
              >
                <RefreshCw className={isFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
              </Button>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center min-h-[40vh]">
                <LoadingSpinner size="lg" />
              </div>
            ) : isError ? (
              <EmptyState
                icon={Bot}
                title={t('agents.loadError.title')}
                description={t('agents.loadError.description')}
                action={
                  <Button onClick={() => refetch()} variant="outline">
                    {t('agents.retry')}
                  </Button>
                }
              />
            ) : filteredAgents.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 pb-6">
                {filteredAgents.map((agent) => (
                  <AgentCard
                    key={agent.id}
                    agent={agent}
                    onLaunch={handleLaunch}
                    onDownload={handleDownload}
                  />
                ))}
              </div>
            ) : (
              <EmptyState
                icon={Bot}
                title={t('agents.empty.title')}
                description={t('agents.empty.description')}
              />
            )}
          </div>
        </div>
      </div>
    </AppShell>
  )
}
